/**
 * STYLING: Plain Tailwind ONLY!
 * Text tool settings: output format pills, writing style pills,
 * toggles (web search, natural style), collapsible advanced (model, temperature).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { PillGroup } from './PillGroup';
import { ToggleSwitch } from './ToggleSwitch';
import { useAvailableModels } from './useAvailableModels';
import {
  OUTPUT_FORMATS, WRITING_STYLES,
  TEXT_STYLES, LENGTH_BUCKETS, READING_LEVELS, GOALS,
  TOOLS_SUPPORTING_GOAL,
} from './textToolSettingsConstants';

interface TextToolSettingsProps {
  toolCode: string;
  model: string;
  onModelChange: (m: string) => void;
  temperature: number;
  onTemperatureChange: (t: number) => void;
  googleSearch: boolean;
  onGoogleSearchChange: (v: boolean) => void;
  outputFormat: string;
  onOutputFormatChange: (v: string) => void;
  writingStyle: string;
  onWritingStyleChange: (v: string) => void;
  naturalStyle: boolean;
  onNaturalStyleChange: (v: boolean) => void;
  // Welle 1 Modifier
  textStyle?: string;
  onTextStyleChange?: (v: string) => void;
  lengthBucket?: string;
  onLengthBucketChange?: (v: string) => void;
  readingLevel?: string;
  onReadingLevelChange?: (v: string) => void;
  goal?: string;
  onGoalChange?: (v: string) => void;
}

export function TextToolSettings({
  toolCode, model, onModelChange, temperature, onTemperatureChange,
  googleSearch, onGoogleSearchChange,
  outputFormat, onOutputFormatChange,
  writingStyle, onWritingStyleChange,
  naturalStyle, onNaturalStyleChange,
  textStyle, onTextStyleChange,
  lengthBucket, onLengthBucketChange,
  readingLevel, onReadingLevelChange,
  goal, onGoalChange,
}: TextToolSettingsProps) {
  const { t } = useTranslation();
  const r = (key: string) => t(`textTools.${key}`);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const showGoal = TOOLS_SUPPORTING_GOAL.has(toolCode);
  const models = useAvailableModels();

  return (
    <div id={`text-tool-settings-${toolCode}`} className="text-tool-settings space-y-4">
      {/* Welle 1 Modifier — Top-Level für direkte UX */}
      {onTextStyleChange && (
        <PillGroup
          label={r('modifiers.style.label')}
          items={TEXT_STYLES.map((s) => ({ id: s.id, label: r(s.labelKey) }))}
          selected={textStyle || ''}
          onSelect={onTextStyleChange}
          allowDeselect
        />
      )}
      {onReadingLevelChange && (
        <PillGroup
          label={r('modifiers.reading.label')}
          items={READING_LEVELS.map((s) => ({ id: s.id, label: r(s.labelKey) }))}
          selected={readingLevel || ''}
          onSelect={onReadingLevelChange}
          allowDeselect
        />
      )}
      {onLengthBucketChange && (
        <PillGroup
          label={r('modifiers.length.label')}
          items={LENGTH_BUCKETS.map((s) => ({ id: s.id, label: r(s.labelKey) }))}
          selected={lengthBucket || ''}
          onSelect={onLengthBucketChange}
          allowDeselect
        />
      )}
      {showGoal && onGoalChange && (
        <PillGroup
          label={r('modifiers.goal.label')}
          items={GOALS.map((s) => ({ id: s.id, label: r(s.labelKey) }))}
          selected={goal || 'standard'}
          onSelect={onGoalChange}
        />
      )}

      {/* Output Format Pills */}
      <PillGroup
        label={r('outputFormat')}
        items={OUTPUT_FORMATS.map((f) => ({ id: f.id, label: r(f.labelKey) }))}
        selected={outputFormat}
        onSelect={onOutputFormatChange}
      />

      {/* Writing Style Pills (Kreativität) */}
      <PillGroup
        label={r('writingStyle')}
        items={WRITING_STYLES.map((s) => ({ id: s.id, label: `${s.icon} ${r(s.labelKey)}` }))}
        selected={writingStyle}
        onSelect={onWritingStyleChange}
      />

      {/* Toggles */}
      <div className="flex gap-5 flex-wrap">
        <ToggleSwitch
          checked={naturalStyle}
          onChange={onNaturalStyleChange}
          label={r('naturalStyle')}
          hint={r('naturalStyleHint')}
        />
        <ToggleSwitch
          checked={googleSearch}
          onChange={onGoogleSearchChange}
          label={r('webSearch')}
          badge="+3 CR"
        />
      </div>

      {/* Advanced Settings (collapsible) */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          {r('advancedSettings')}
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">{r('model')}</label>
              <select
                value={model} onChange={(e) => onModelChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
              >
                {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                {r('temperature')}: {temperature.toFixed(1)}
              </label>
              <input
                type="range" value={temperature}
                onChange={(e) => onTemperatureChange(parseFloat(e.target.value))}
                min={0} max={2} step={0.1}
                className="w-full accent-primary-500"
              />
              <p className="text-2xs text-neutral-400 mt-1">{r('temperatureOverride')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

